import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface WorkoutActivity {
  id: string;
  workout_type: string | null;
  exercises_completed: number | null;
  duration_minutes: number | null;
  calories_burned: number | null;
  completed_at: string;
  notes: string | null;
}

export interface MessageActivity {
  id: string;
  text: string;
  is_ai: boolean;
  created_at: string;
}

export interface NutritionActivity {
  id: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  meal_type: string | null;
  logged_date: string;
  notes: string | null;
}

export interface WeightActivity {
  id: string;
  weight: number;
  logged_at: string;
  notes: string | null;
}

export interface BadgeActivity {
  id: string;
  badge_id: string;
  unlocked_at: string;
}

export interface ActivitySummary {
  totalWorkouts: number;
  totalMessages: number;
  userMessages: number;
  totalNutritionLogs: number;
  totalBadges: number;
  averageCalories: number;
  totalWorkoutMinutes: number;
}

export interface UserActivity {
  workouts: WorkoutActivity[];
  messages: MessageActivity[];
  nutritionLogs: NutritionActivity[];
  weightHistory: WeightActivity[];
  badges: BadgeActivity[];
  summary: ActivitySummary;
}

export const useAdminUserActivity = () => {
  const [activity, setActivity] = useState<UserActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-user-activity?userId=${userId}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to fetch activity');
      }

      const data: UserActivity = await res.json();
      setActivity(data);
      return data;
    } catch (err) {
      console.error('Error fetching user activity:', err);
      const message = err instanceof Error ? err.message : 'Failed to fetch activity';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearActivity = useCallback(() => {
    setActivity(null);
    setError(null);
  }, []);

  return {
    activity,
    loading,
    error,
    fetchActivity,
    clearActivity,
  };
};
